/**
 * Business Objective Edit Route
 *
 * Edit an existing business objective using BusinessObjectiveForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBusinessObjective } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { BusinessObjectiveForm } from '../../components/business-objectives';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updateBusinessObjective,
  type UpdateBusinessObjectiveInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessObjectiveFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/business-objectives/$businessObjectiveId/edit')({
  component: BusinessObjectiveEditPage,
});

function BusinessObjectiveEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { businessObjectiveId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch business objective data
  const { data, loading, error: fetchError } = useBusinessObjective(businessObjectiveId);
  const businessObjective = data?.businessObjective;

  if (loading) {
    return (
      <div className="business-objective-edit-page">
        <LoadingSpinner centered message="Loading business objective..." />
      </div>
    );
  }

  if (fetchError || !businessObjective) {
    return (
      <div className="business-objective-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load business objective"
          description={fetchError?.message || 'Business objective not found'}
          actionLabel="Back to Business Objectives"
          onAction={() =>
            navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateBusinessObjectiveFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateBusinessObjectiveInput = {
        name: formData.name,
        description: formData.description || undefined,
        objectiveType: formData.objectiveType,
        priority: formData.priority || undefined,
        status: formData.status || undefined,
        startDate: formData.startDate || undefined,
        targetDate: formData.targetDate || undefined,
        timeHorizon: formData.timeHorizon || undefined,
        successCriteria: formData.successCriteria || undefined,
        kpiStatement: formData.kpiStatement || undefined,
        ownerId: formData.ownerId,
        owningBusinessUnitId: formData.owningBusinessUnitId || undefined,
        sponsorId: formData.sponsorId || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updateBusinessObjective(businessObjectiveId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetBusinessObjective', 'ListBusinessObjectives'],
      });

      // Navigate back to business objective detail page
      navigate({ to: `/business-objectives/${businessObjectiveId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/business-objectives/${businessObjectiveId}` as string });
  };

  // Map business objective data to form default values
  const defaultValues: Partial<CreateBusinessObjectiveFormData> = {
    name: businessObjective.name,
    description: businessObjective.description || undefined,
    objectiveType:
      businessObjective.objectiveType as CreateBusinessObjectiveFormData['objectiveType'],
    priority: businessObjective.priority as CreateBusinessObjectiveFormData['priority'],
    status: businessObjective.status as CreateBusinessObjectiveFormData['status'],
    startDate: businessObjective.startDate || undefined,
    targetDate: businessObjective.targetDate || undefined,
    timeHorizon: businessObjective.timeHorizon as CreateBusinessObjectiveFormData['timeHorizon'],
    successCriteria: businessObjective.successCriteria || undefined,
    kpiStatement: businessObjective.kpiStatement || undefined,
    ownerId: businessObjective.ownerId,
    owningBusinessUnitId: businessObjective.owningBusinessUnitId || undefined,
    sponsorId: businessObjective.sponsorId || undefined,
    tags: businessObjective.tags || [],
    notes: businessObjective.notes || undefined,
  };

  return (
    <div className="business-objective-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Business Objective</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update business objective information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessObjectiveForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
